import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { format } from 'date-fns';
import { Check, ChevronsUpDown } from 'lucide-react';

import { MasterNameApi } from '@/api';
import { FlagsApi } from '@/api/api.flags';
import { Badge } from '@/components/TailwindComponents/base/Badge';
import { Button } from '@/components/TailwindComponents/base/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/TailwindComponents/base/Command';
import { Label } from '@/components/TailwindComponents/base/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover';
import { Textarea } from '@/components/TailwindComponents/base/Textarea';
import { SidePaneModal } from '@/components/TailwindComponents/components/Modals/SidePaneModal';
import { cn } from '@/components/TailwindComponents/lib/utils';
import {
  IFlagOption,
  IPersonFlagRequest,
  IPersonFlag,
  IPersonFlagUpdateRequest,
  FlagOrigin,
  SystemFlagType,
  IMasterNamePerson,
} from '@/models';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { canUserAssignFlag, canUserEditFlag, formatPersonName } from '@/utils';

interface FlagFormData {
  flagId: string;
  reason: string;
}

interface FlagSidePanelProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  personId: string;
  flag?: IPersonFlag | null;
  mode?: 'add' | 'view' | 'edit';
}

export const FlagSidePanel: FC<FlagSidePanelProps> = ({
  open,
  onClose,
  onSave,
  personId,
  flag = null,
  mode = 'add',
}) => {
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const [person, setPerson] = useState<IMasterNamePerson | null>(null);
  const [availableFlags, setAvailableFlags] = useState<IFlagOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [flagSelectOpen, setFlagSelectOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const popoverContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FlagFormData>({
    defaultValues: {
      flagId: '',
      reason: '',
    },
  });

  const watchedFlagId = watch('flagId');

  const fetchPerson = async () => {
    const p = await MasterNameApi.getMasterName(personId);
    setPerson(p.data);
  };

  const fetchAvailableFlags = async () => {
    if (!personId) return;

    try {
      setLoading(true);
      const response = await FlagsApi.getAvailableFlags(personId);
      setAvailableFlags(response.data || []);
    } catch (error: any) {
      updateToast({
        open: true,
        message: error.message || 'Failed to fetch available flags',
        type: ConfirmTypes.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: FlagFormData) => {
    if (mode === 'add') {
      if (!canUserAssignFlag(user)) {
        updateToast({
          open: true,
          message: 'You do not have permission to assign flags',
          type: ConfirmTypes.ERROR,
        });
        return;
      }

      try {
        const request: IPersonFlagRequest = {
          flagId: data.flagId,
          reason: data.reason,
        };

        await FlagsApi.assignPersonFlag(personId, request);

        updateToast({
          open: true,
          message: 'Flag assigned successfully',
          type: ConfirmTypes.SUCCESS,
        });

        onSave();
        handleClose();
      } catch (error: any) {
        updateToast({
          open: true,
          message: error.message || 'Failed to assign flag',
          type: ConfirmTypes.ERROR,
        });
      }
    } else if (mode === 'edit' && flag) {
      if (!canUserEditFlag(user, flag)) {
        updateToast({
          open: true,
          message: 'You do not have permission to edit this flag',
          type: ConfirmTypes.ERROR,
        });
        return;
      }

      try {
        const request: IPersonFlagUpdateRequest = {
          reason: data.reason,
        };

        await FlagsApi.updatePersonFlag(personId, flag._id, request);
        updateToast({
          open: true,
          message: 'Flag updated successfully',
          type: ConfirmTypes.SUCCESS,
        });
        onSave();
        handleClose();
      } catch (error: any) {
        const is404 = error?.response?.status === 404;
        const errorMessage = is404
          ? 'This flag assignment no longer exists. The list will be refreshed.'
          : error.message || 'Failed to update flag';

        updateToast({
          open: true,
          message: errorMessage,
          type: ConfirmTypes.ERROR,
        });

        if (is404) {
          setTimeout(() => onSave(), 1000);
          handleClose();
        }
      }
    }
  };

  const handleClose = () => {
    reset({ flagId: '', reason: '' });
    setSearchQuery('');
    setFlagSelectOpen(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      if (mode === 'add') {
        reset({ flagId: '', reason: '' });
        fetchAvailableFlags();
        fetchPerson();
      } else if ((mode === 'view' || mode === 'edit') && flag) {
        reset({
          flagId: flag.flag._id,
          reason: flag.reason || '',
        });
      }
    }
  }, [open, mode, flag, reset]);

  const filteredFlags = useMemo(() => {
    return availableFlags
      .filter((f) => !(person?.isWanted && f.code === 'Wanted'))
      .filter((f) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          f.name.toLowerCase().includes(query) ||
          f.code.toLowerCase().includes(query)
        );
      });
  }, [availableFlags, person?.isWanted, searchQuery]);

  const getSelectedFlag = (flagId: string) => {
    return availableFlags.find((f) => f._id === flagId);
  };

  const getTitle = () => {
    switch (mode) {
      case 'edit':
        return 'Edit Flag';
      case 'view':
        return 'View Flag';
      default:
        return 'Add Flag';
    }
  };

  const getAddedByText = (flagData: IPersonFlag) => {
    if (flagData.origin === FlagOrigin.SYSTEM) {
      const systemTypeMap = {
        [SystemFlagType.WANTED_PERSON]: 'System: Wanted Person',
        [SystemFlagType.JMS_RISK_ASSESSMENT]: 'System: JMS Risk Assessment',
        [SystemFlagType.JMS_MEDICAL_ASSESSMENT]:
          'System: JMS Medical Assessment',
      };
      return flagData.systemType
        ? systemTypeMap[flagData.systemType]
        : 'System';
    }
    return flagData.addedBy ? formatPersonName(flagData.addedBy) : 'Manual';
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isAddMode = mode === 'add';

  return (
    <SidePaneModal
      title={getTitle()}
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
    >
      <SidePaneModal.Content>
        <div ref={popoverContainerRef}>
          <form
            id="flag-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
          >
            {isAddMode && (
              <div className="space-y-2">
                <Label className="font-medium">Flag Name</Label>
                <Controller
                  name="flagId"
                  control={control}
                  rules={{ required: 'Please select a flag' }}
                  render={({ field }) => {
                    const selectedFlag = getSelectedFlag(field.value);
                    return (
                      <Popover
                        open={flagSelectOpen}
                        onOpenChange={setFlagSelectOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={flagSelectOpen}
                            disabled={loading}
                            className="w-full justify-between font-normal"
                          >
                            {selectedFlag ? (
                              <span className="flex items-center gap-2">
                                {selectedFlag.name}
                                <span className="rounded bg-red-500 px-1.5 py-0.5 text-xs text-white">
                                  {selectedFlag.code}
                                </span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                Search flags...
                              </span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          container={popoverContainerRef.current ?? undefined}
                          className="z-[2301] w-[var(--radix-popover-trigger-width)] p-0"
                        >
                          <Command shouldFilter={false}>
                            <CommandInput
                              value={searchQuery}
                              onValueChange={setSearchQuery}
                            />
                            <CommandList
                              onWheel={(e) => e.stopPropagation()}
                              className="max-h-[200px] overflow-auto"
                            >
                              <CommandEmpty>
                                {loading ? 'Loading...' : 'No flags found.'}
                              </CommandEmpty>
                              <CommandGroup>
                                {filteredFlags.map((f) => (
                                  <CommandItem
                                    key={f._id}
                                    value={f._id}
                                    onSelect={() => {
                                      field.onChange(f._id);
                                      setFlagSelectOpen(false);
                                      setSearchQuery('');
                                    }}
                                  >
                                    <span className="flex items-center gap-2">
                                      {f.name}
                                      <span className="rounded bg-red-500 px-1.5 py-0.5 text-xs text-white">
                                        {f.code}
                                      </span>
                                    </span>
                                    <Check
                                      className={cn(
                                        'ml-auto h-4 w-4',
                                        field.value === f._id
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    );
                  }}
                />
                {errors.flagId && (
                  <p className="text-sm text-destructive">
                    {errors.flagId.message}
                  </p>
                )}
              </div>
            )}

            {(isViewMode || isEditMode) && flag && (
              <>
                <div className="space-y-2">
                  <Label className="font-medium">Flag Name</Label>
                  <p className="text-sm flex items-center gap-2">
                    {flag.flag.name}
                    <span className="rounded bg-red-500 px-1.5 py-0.5 text-xs text-white">
                      {flag.flag.code}
                    </span>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium">Type</Label>
                  <div>
                    <Badge
                      variant={
                        flag.origin === FlagOrigin.SYSTEM
                          ? 'default'
                          : 'secondary'
                      }
                    >
                      {flag.origin}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium">Added By</Label>
                  <p className="text-sm">{getAddedByText(flag)}</p>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium">Date Assigned</Label>
                  <p className="text-sm">
                    {format(new Date(flag.dateAssigned), 'MM/dd/yyyy HH:mm')}
                  </p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label className="font-medium">Reason</Label>
              {isViewMode ? (
                <p className="text-sm">{flag?.reason || ''}</p>
              ) : (
                <Controller
                  name="reason"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 500,
                      message: 'Reason should be less than 500 characters.',
                    },
                  }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      placeholder="Enter reason for flag assignment..."
                      rows={4}
                      className="resize-none"
                    />
                  )}
                />
              )}
              {errors.reason && (
                <p className="text-sm text-destructive">
                  {errors.reason.message}
                </p>
              )}
            </div>
          </form>
        </div>
      </SidePaneModal.Content>

      <SidePaneModal.Footer>
        <Button type="button" variant="outline" onClick={handleClose}>
          {isViewMode ? 'Close' : 'Cancel'}
        </Button>
        {!isViewMode && (
          <Button
            type="submit"
            form="flag-form"
            variant="destructive"
            disabled={isSubmitting || (isAddMode && !watchedFlagId)}
          >
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update Flag' : 'Save'}
          </Button>
        )}
      </SidePaneModal.Footer>
    </SidePaneModal>
  );
};
